import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { select, Store } from '@ngrx/store';

import { includes, isNil, isNull, isUndefined } from 'lodash-es';
import { combineLatest, map, Observable, of } from 'rxjs';
import { concatMap, filter } from 'rxjs/operators';

import { AddressEnum } from '../../../../enums/address';
import { MainTabBasePathEnum } from '../../../../enums/main-tabs';
import { ProfileSearchEnum } from '../../../../enums/profile-search';
import * as fromPropertyProfileActions from '../../../../store/actions/property-profile.actions';
import { ParsedComparableSale, PropertyProfile } from '../../../../store/models/property-profile.model';
import * as fromMapSelectors from '../../../../store/selectors/map.selectors';
import * as fromPropertyProfileSelectors from '../../../../store/selectors/property-profile.selectors';
import { AddressPipe } from '../../../pipes/address.pipe';
import { MapsService } from '../../../services/maps.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-profile-comparable-sales',
  standalone: false,
  templateUrl: './comparable-sales.page.html'
})
export class ComparableSalesPageComponent {
  private activatedRoute = inject(ActivatedRoute);
  private addressPipe = inject(AddressPipe);
  private mapsService = inject(MapsService);
  private store = inject(Store);

  comparableSalesBounds: google.maps.LatLngBounds | null = null;
  mapInstance: google.maps.Map | null = null;
  profileId: string | null;
  selectedComparableSale: ParsedComparableSale | null = null;
  selectedMapTypeId: google.maps.MapTypeId | null = null;
  selectedTab: 'list' | 'map' = 'list';
  defaultBackHref: string | undefined;

  mapCenter$: Observable<google.maps.LatLngLiteral>;
  comparableSales$: Observable<ParsedComparableSale[]>;
  comparableSalesMarkerOptions$: Observable<google.maps.MarkerOptions>;
  mapOptions$: Observable<google.maps.MapOptions>;
  markerOptions$: Observable<google.maps.MarkerOptions>;
  profile$: Observable<PropertyProfile>;

  constructor() {
    this.defaultBackHref = this.activatedRoute.snapshot.data['defaultBackHref'] ?? MainTabBasePathEnum.PropertySearch;
    this.profileId = this.activatedRoute.snapshot.paramMap.get('id');

    if (this.profileId) {
      combineLatest([
        this.store.pipe(select(fromPropertyProfileSelectors.selectPropertyProfileMapCenter(this.profileId))),
        this.store.pipe(select(fromPropertyProfileSelectors.selectPropertyProfileComparableSales(this.profileId)))
      ]).subscribe(([mapCenter, comparableSales]) => {
        this.comparableSalesBounds = new google.maps.LatLngBounds();
        this.comparableSalesBounds.extend(mapCenter);
        for (let i = 0; i < comparableSales.length; i++) {
          this.comparableSalesBounds.extend(comparableSales[i].location);
        }
        this.centerOnBounds();
      });
    }

    this.profile$ = of(this.profileId).pipe(
      filter((id) => !isNil(id)),
      concatMap((id) => this.store.pipe(select(fromPropertyProfileSelectors.selectPropertyProfile(id)))),
      filter((propertyProfile) => !isUndefined(propertyProfile))
    );

    this.mapCenter$ = of(this.profileId).pipe(
      filter((id) => !isNil(id)),
      concatMap((id) => this.store.pipe(select(fromPropertyProfileSelectors.selectPropertyProfileMapCenter(id))))
    );

    this.comparableSales$ = of(this.profileId).pipe(
      filter((id) => !isNil(id)),
      concatMap((id) => this.store.pipe(select(fromPropertyProfileSelectors.selectPropertyProfileComparableSales(id))))
    );

    this.mapOptions$ = this.store.pipe(select(fromMapSelectors.selectOptions));

    this.markerOptions$ = this.store.pipe(
      select(fromMapSelectors.selectMarkerOptions),
      map((markerOptions) => ({
        ...markerOptions,
        icon: {
          scaledSize: new google.maps.Size(26, 44),
          url: '/assets/img/pin-home-special.png'
        }
      }))
    );

    this.comparableSalesMarkerOptions$ = this.store.pipe(
      select(fromMapSelectors.selectMarkerOptions),
      map((markerOptions) => ({
        ...markerOptions,
        icon: {
          scaledSize: new google.maps.Size(26, 44),
          url: '/assets/img/pin-home.png'
        }
      }))
    );

    this.store.pipe(select(fromMapSelectors.selectCurrentType)).subscribe((selectedMapTypeId) => {
      this.selectedMapTypeId = selectedMapTypeId;
      this.updateMapType();
    });
  }

  async ionViewWillEnter() {
    await this.mapsService.enableWatchPosition();
  }

  async ionViewWillLeave() {
    await this.mapsService.disableWatchPosition();
  }

  mapInitialized(mapInstance: google.maps.Map) {
    this.mapInstance = mapInstance;
    this.centerOnBounds();
    this.updateMapType();
  }

  updateMapType() {
    if (!isNull(this.selectedMapTypeId)) {
      this.mapInstance?.setMapTypeId(this.selectedMapTypeId);
    }
  }

  centerOnBounds() {
    if (!isNull(this.mapInstance) && !isNull(this.comparableSalesBounds) && !this.comparableSalesBounds.isEmpty()) {
      this.mapInstance.fitBounds(this.comparableSalesBounds);
    }
  }

  segmentChange(event: CustomEvent) {
    this.selectedTab = event.detail.value;
  }

  selectComparableSaleMarker(comparableSale: ParsedComparableSale) {
    this.selectedComparableSale = comparableSale;
  }

  loadProfile(comparableSale: ParsedComparableSale) {
    this.store.dispatch(
      fromPropertyProfileActions.loadProfileByAddress({
        payload: {
          address: this.addressPipe.transform(comparableSale, AddressEnum.LineOne),
          searchType: includes(this.defaultBackHref, MainTabBasePathEnum.WalkingFarm)
            ? ProfileSearchEnum.WalkingFarm
            : ProfileSearchEnum.PropertySearch,
          zip: this.addressPipe.transform(comparableSale, AddressEnum.ZipFull)
        }
      })
    );
  }

  clearSearch() {
    this.selectedComparableSale = null;
  }

  protected readonly addressType = AddressEnum;
}
